/**
 * Atlas PM · 任务详情 · 要素「完成度 / 质量 / 附件」控件（P6.41）
 *
 * ⭐ 完成度与质量是**用户填**的两个平行字段：
 *      完成度 → 指标完成度 → 任务完成度
 *      质量   → 指标质量   → 任务质量（互不影响）
 *    写入走 PUT /elements/:id/value（带 completionPct / qualityPct / version 乐观锁），
 *    后端在同事务内重算全部指标与任务进度。
 *
 * 附件（复用 attachment 表，ownerType=ELEMENT）：
 *      新增 POST /elements/:id/attachments（先走 storage 两段式上传）
 *      删除 DELETE /attachments/:id
 *      下载 GET  /storage/files/:id/download-url
 *    任务冻结后增删被拒（403 TASK_FROZEN），但下载始终可用。
 */

import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../../api/client';
import type { ElementInfo } from '@atlaspm/shared';
import {
  useAddElementAttachment,
  useDeleteElementAttachment,
  useSetElementValue,
} from './useTaskDetail';
import { uploadFile, fmtSize } from './fileUpload';
import { uploadErrorText } from './uploadErrorText';

export interface ElementProgressControlProps {
  taskId: string;
  element: ElementInfo;
  /** 是否可写（冻结/审批中为 false，仅影响前端置灰；后端四层防线兜底） */
  canEdit: boolean;
}

function clampPct(raw: string, prev: number): number {
  if (raw.trim() === '') return 0;
  const n = Number(raw);
  if (!Number.isFinite(n)) return prev;
  return Math.max(0, Math.min(100, Math.round(n * 100) / 100));
}

function errText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.code === 'TASK_FROZEN') return '任务已冻结，不可修改';
    if (err.code === 'TASK_LOCKED') return '审批中，暂不可修改';
    return err.message || fallback;
  }
  return uploadErrorText(err, fallback);
}

export default function ElementProgressControl({
  taskId,
  element,
  canEdit,
}: ElementProgressControlProps) {
  const setValue = useSetElementValue(taskId);
  const addAtt = useAddElementAttachment(taskId);
  const delAtt = useDeleteElementAttachment(taskId);

  const version = element.value?.version;
  const [completion, setCompletion] = useState(String(element.completionPct ?? 0));
  const [quality, setQuality] = useState(String(element.qualityPct ?? 0));
  const [err, setErr] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setCompletion(String(element.completionPct ?? 0));
    setQuality(String(element.qualityPct ?? 0));
    setErr(null);
  }, [element.completionPct, element.qualityPct]);

  const commitPct = (field: 'completionPct' | 'qualityPct', raw: string) => {
    const prev = field === 'completionPct' ? element.completionPct : element.qualityPct;
    const next = clampPct(raw, prev);
    if (next === prev) return;
    setErr(null);
    const args =
      field === 'completionPct'
        ? { elementId: element.id, completionPct: next, version }
        : { elementId: element.id, qualityPct: next, version };
    setValue.mutate(args, { onError: (e) => setErr(errText(e, '保存失败')) });
  };

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    setErr(null);
    setUploading(true);
    try {
      const up = await uploadFile(file, { taskId, usage: 'ELEMENT_ATTACHMENT' });
      await addAtt.mutateAsync({ elementId: element.id, fileId: up.fileId });
    } catch (e) {
      setErr(errText(e, '上传失败'));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const download = async (fileId: string) => {
    setErr(null);
    try {
      const { downloadUrl } = await api.get<{ downloadUrl: string }>(
        `/storage/files/${fileId}/download-url`,
      );
      window.open(downloadUrl, '_blank', 'noopener');
    } catch (e) {
      setErr(errText(e, '获取下载链接失败'));
    }
  };

  const removeAtt = (attachmentId: string) => {
    setErr(null);
    delAtt.mutate(attachmentId, { onError: (e) => setErr(errText(e, '删除失败')) });
  };

  const attachments = element.attachments ?? [];
  const busy = setValue.isPending || addAtt.isPending || delAtt.isPending || uploading;
  const disabled = !canEdit || busy;

  return (
    <div className="dt-elprog" data-el-id={element.id}>
      <div className="dt-elprog__nums">
        <label className="dt-elprog__num">
          <span>完成度</span>
          <input
            type="number"
            min={0}
            max={100}
            className="dt-elprog__in"
            value={completion}
            disabled={disabled}
            aria-label="要素完成度"
            onChange={(e) => setCompletion(e.target.value)}
            onBlur={() => commitPct('completionPct', completion)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
          <span className="dt-elprog__u">%</span>
        </label>
        <label className="dt-elprog__num">
          <span>质量</span>
          <input
            type="number"
            min={0}
            max={100}
            className="dt-elprog__in"
            value={quality}
            disabled={disabled}
            aria-label="要素完成质量"
            onChange={(e) => setQuality(e.target.value)}
            onBlur={() => commitPct('qualityPct', quality)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
          />
          <span className="dt-elprog__u">%</span>
        </label>
      </div>

      <div className="dt-elprog__att">
        <span className="dt-elprog__att-hd">
          附件 {element.requireAttachment && <em className="dt-elprog__need">需附件</em>}
          <span className="dt-elprog__count">{attachments.length}</span>
        </span>

        {attachments.map((a) => (
          <div className="dt-elprog__file" key={a.id}>
            <button
              type="button"
              className="dt-elprog__fname"
              title="下载"
              onClick={() => void download(a.fileId)}
            >
              📎 {a.fileName || `#${a.fileId}`}
            </button>
            <span className="dt-elprog__fsize">{fmtSize(a.sizeBytes)}</span>
            {canEdit && (
              <button
                type="button"
                className="dt-elprog__fdel"
                aria-label="删除附件"
                disabled={busy}
                onClick={() => removeAtt(a.id)}
              >
                ✕
              </button>
            )}
          </div>
        ))}

        {canEdit && (
          <label className={['dt-elprog__add', disabled ? 'is-disabled' : ''].join(' ')}>
            {uploading ? '上传中…' : '＋ 添加附件'}
            <input
              ref={fileRef}
              type="file"
              hidden
              disabled={disabled}
              onChange={(e) => void onFile(e.target.files?.[0])}
            />
          </label>
        )}
      </div>

      {err && <span className="dt-elprog__err">⚠ {err}</span>}
    </div>
  );
}
